import { CARDS, type CardDef } from '../game/config/cards';

export type CardPackType = 'common' | 'rare';

export interface CardCollectionState {
  ownedCounts: Record<string, number>;
  equippedCardIds: string[];
}

export const CARD_PACK_PRICES: Record<CardPackType, number> = {
  common: 50,
  rare: 150,
};

const CARD_COLLECTION_STORAGE_KEY = 'henry_card_collection';
const MAX_EQUIPPED_CARDS = 3;

const DEFAULT_COLLECTION_STATE: CardCollectionState = {
  ownedCounts: {},
  equippedCardIds: [],
};

function normalizeState(data: Partial<CardCollectionState> | null): CardCollectionState {
  if (!data) return { ...DEFAULT_COLLECTION_STATE };

  const ownedCounts = Object.entries(data.ownedCounts ?? {}).reduce<Record<string, number>>((acc, [cardId, count]) => {
    const safeCount = Math.max(0, Math.floor(count ?? 0));
    if (safeCount > 0) acc[cardId] = safeCount;
    return acc;
  }, {});

  const discoveredCardIds = new Set(Object.keys(ownedCounts));
  const equippedCardIds = (data.equippedCardIds ?? []).filter((id) => discoveredCardIds.has(id)).slice(0, MAX_EQUIPPED_CARDS);

  return {
    ownedCounts,
    equippedCardIds,
  };
}

export function readCardCollectionState(): CardCollectionState {
  try {
    const raw = localStorage.getItem(CARD_COLLECTION_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLLECTION_STATE };
    const parsed = JSON.parse(raw) as Partial<CardCollectionState>;
    return normalizeState(parsed);
  } catch {
    return { ...DEFAULT_COLLECTION_STATE };
  }
}

export function writeCardCollectionState(state: CardCollectionState): void {
  localStorage.setItem(CARD_COLLECTION_STORAGE_KEY, JSON.stringify(normalizeState(state)));
}

export function addOwnedCardToCollection(state: CardCollectionState, cardId: string): CardCollectionState {
  const currentCount = state.ownedCounts[cardId] ?? 0;
  return {
    ...state,
    ownedCounts: {
      ...state.ownedCounts,
      [cardId]: currentCount + 1,
    },
  };
}

export function toggleEquippedCard(state: CardCollectionState, cardId: string): CardCollectionState {
  if ((state.ownedCounts[cardId] ?? 0) <= 0) {
    return state;
  }

  const isEquipped = state.equippedCardIds.includes(cardId);
  if (isEquipped) {
    return {
      ...state,
      equippedCardIds: state.equippedCardIds.filter((id) => id !== cardId),
    };
  }

  if (state.equippedCardIds.length >= MAX_EQUIPPED_CARDS) {
    return {
      ...state,
      equippedCardIds: [...state.equippedCardIds.slice(1), cardId],
    };
  }

  return {
    ...state,
    equippedCardIds: [...state.equippedCardIds, cardId],
  };
}

export function getDiscoveredCards(state: CardCollectionState): CardDef[] {
  return CARDS.filter((card) => (state.ownedCounts[card.id] ?? 0) > 0);
}

function randomItem<T>(items: T[], emptyErrorContext: string): T {
  if (items.length === 0) {
    throw new Error(`Cannot draw card: no cards available in ${emptyErrorContext}`);
  }
  return items[Math.floor(Math.random() * items.length)];
}

function drawRarity(packType: CardPackType): CardDef['rarity'] {
  const roll = Math.random();
  if (packType === 'common') {
    if (roll < 0.7) return 'common';
    if (roll < 0.95) return 'rare';
    return 'epic';
  }

  if (roll < 0.65) return 'rare';
  if (roll < 0.95) return 'epic';
  return 'legendary';
}

export function drawCardFromPack(packType: CardPackType): CardDef {
  const rarity = drawRarity(packType);
  const rarityPool = CARDS.filter((card) => card.rarity === rarity);
  if (rarityPool.length > 0) return randomItem(rarityPool, `${rarity} rarity pool`);
  return randomItem(CARDS, 'global card pool');
}
